/*
 * Copyright (c) 2019. Nedaware SL
 */

import {finalize} from 'rxjs/operators';
import {CollectionViewer, DataSource} from '@angular/cdk/collections';
import {BehaviorSubject, Observable} from 'rxjs';
import {Paginated} from './collection-answer';
import {EntityLoader} from './entity-loader';
import {EntityInterface} from './entity-interface';
import {HttpResponse} from '@angular/common/http';
import {Resource} from "./resource";
import {FilterList} from "./filter-list";

export class EntityDatasource<T = Resource, I extends EntityInterface<T> = EntityInterface<T>> implements DataSource<T> {
    public loader: EntityLoader<T, I>;
    /** BehaviorSubject en el que podemos escuchar las actualizaciones de la Entity */
    private _entitiesBehavior = new BehaviorSubject<T[]>([]);
    /** BehaviorSubject en el que podemos escuchar las actualizaciones de loading */
    private _loadingBehavior = new BehaviorSubject<boolean>(false);
    /** Current sorting configuration */
    private _sorting: { attr: string, dir: 'asc' | 'desc' } = {attr: null, dir: null};
    /** Current pagination to use */
    private _pagination: PaginationInfo = {pageIndex: 0, pageSize: null};

    /** BehaviorSubject en el que podemos escuchar las actualizaciones de paginado según las respuestas del servidor */
    public serverPagination = new BehaviorSubject<PaginationInfo>(null);
    /** Indica si el datasource está cargando nuevos datos o no */
    public loading$ = this._loadingBehavior.asObservable();
    /** Actualizaciones de entidades */
    public entities$ = this._entitiesBehavior.asObservable();
    /** Número de items total */
    public totalItems = 0;
    /** Filtros aplicados */
    public filtros: FilterList = null;

    /**
     * Se puede construír el data source a partir de un servicio ya creado
     * o, alternativamente, se puede enviar una interfaz de entidad y el ApiService
     * para que el datasource cree un EntityLoader para la interfaz dada
     */
    constructor(param: EntityLoader<T, I>) {
        this.loader = param;
    }

    public updateItem(item) {
        const res = this._entitiesBehavior.value.map(value => {
            if (value instanceof Resource && value.iri.toString() === item.iri.toString()) {
                return item;
            }
            return value;
        });
        this._entitiesBehavior.next(res);
    }

    /** Solicita nuevos datos al servidor */
    public load() {
        this._load(this._pagination);
    }

    /**
     * Requiere a la API ordenación según la información indicada,
     * la api puede aceptarla o no
     */
    public sortData(event: { active: string, direction: 'asc' | 'desc' | '' }) {
        this.sorting = event; // setter
        this.load();
    }

    private _load(pagination: PaginationInfo) {
        this._loadingBehavior.next(true);
        this.loader
            .find(
                this.filtros,
                this._sorting.attr,
                this._sorting.dir,
                pagination.pageIndex,
                pagination.pageSize)
            .pipe(
                // Al acabar loading vuelve a false
                finalize(() => this._loadingBehavior.next(false))
            )
            .subscribe((res: Paginated<T>) => {
                this.totalItems = res.totalItems;

                // No haremos nada si sabemos que la página está más allá de la última página,
                // esto previene la paginación de cambiar a un valor incorrecto.
                if (!res.lastPage || !res.currentPage || res.currentPage <= res.lastPage) {
                    this._pagination = {
                        pageSize: res.isLastPage() ? pagination.pageSize : res.member.length,
                        pageIndex: res.currentPage ? res.currentPage - 1 : 0
                    };

                    this._entitiesBehavior.next(res.member);
                } else {
                    console.warn(`Paginación más allá de la última página.`);
                }

                this._notifyPaginationIfDifferent(pagination, this._pagination);
            });
    }

    /**
     * Notifica la paginación recibida si no coincide con la solicitada
     */
    private _notifyPaginationIfDifferent(requestedPagination: PaginationInfo, receivedPagination: PaginationInfo) {
        if (requestedPagination.pageSize !== receivedPagination.pageSize ||
            requestedPagination.pageIndex !== receivedPagination.pageIndex) {
            // Si pageSize era null realmente no hemos solicitado ninguna paginación concreta
            if (requestedPagination.pageSize !== null) {
                console.warn(
                    `Paginación del servidor distinta a la solicitada. ` +
                    `Compruebe que la API admite el tamaño de página ${requestedPagination.pageSize} ` +
                    `en el endpoint ${this.loader.entityInterface.endpoint}.`,
                    'Solicitada:', requestedPagination, 'Recibida:', receivedPagination);
            }
            this.serverPagination.next(receivedPagination);
        }
    }

    /**
     * Realiza DEL
     */
    public remove(model: T): Observable<HttpResponse<any>> {
        this._loadingBehavior.next(true);
        return this.loader
            .remove(model)
            .pipe(
                finalize(() => this._loadingBehavior.next(false))
            );
    }

    connect(collectionViewer: CollectionViewer): Observable<T[]> {
        return this._entitiesBehavior.asObservable();
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this._entitiesBehavior.complete();
        this._loadingBehavior.complete();
    }

    /**
     * OJO: Esto no provocará un reload y por tanto la paginación no será validada contra el servidor!
     */
    set pagination(value: PaginationInfo) {
        this._pagination = value;
    }

    get pagination(): PaginationInfo {
        return this._pagination;
    }

    /**
     * OJO: Esto no provacará un reload!
     */
    set sorting(value: { active: string, direction: 'asc' | 'desc' | '' }) {
        this._sorting = {
            attr: value.active ? value.active : null,
            dir: value.direction !== '' ? value.direction : null
        };
    }
}

export interface PaginationInfo {
    /** The current page index */
    pageIndex: number;
    /** The current page size */
    pageSize: number;
}
